import { z } from 'zod';
import { isoDateSchema } from './common.js';
/**
 * The six achievements.
 *
 * These are seed data for an `achievements` collection, not a hard-coded list the engine
 * switches on — the portal can edit their copy without a release. The KEYS are fixed,
 * because the conditions are code; the titles and descriptions are content.
 *
 * If the schedule slips, CLAUDE.md §9 cuts everything past the first three.
 */
export const ACHIEVEMENT_KEYS = [
    'FIRST_STEP',
    'STREAK_3',
    'STREAK_9',
    'STREAK_27',
    'STREAK_81',
    'FIRST_JOURNEY',
    'STORY_EXPLORER',
    'JOURNEY_COLLECTOR',
];
export const achievementKeySchema = z.enum(ACHIEVEMENT_KEYS);
/**
 * What each one is measured against. The engine reads `metric` and `threshold` rather than
 * branching per key, so a seventh achievement is a row, not a code change.
 *
 *   STREAK          the user's current streak, in their own local days
 *   DAYS_COMPLETED  days finished across every journey
 *   STORIES         stories completed (one per day in the MVP, but counted separately
 *                   because a day gains more activities later)
 *   JOURNEYS        journeys completed
 */
export const achievementMetricSchema = z.enum(['STREAK', 'DAYS_COMPLETED', 'STORIES', 'JOURNEYS']);
export const SEED_ACHIEVEMENTS = [
    {
        key: 'FIRST_STEP',
        metric: 'DAYS_COMPLETED',
        threshold: 1,
        title: 'First Step',
        description: 'Complete your first day',
        order: 0,
    },
    {
        key: 'STREAK_3',
        metric: 'STREAK',
        threshold: 3,
        title: '3-Day Streak',
        description: 'Maintain a 3-day streak',
        order: 1,
    },
    {
        key: 'STREAK_9',
        metric: 'STREAK',
        threshold: 9,
        title: '9-Day Streak',
        description: 'Maintain a 9-day streak',
        order: 2,
    },
    {
        key: 'STREAK_27',
        metric: 'STREAK',
        threshold: 27,
        title: '27-Day Streak',
        description: 'Maintain a 27-day streak',
        order: 3,
    },
    {
        key: 'STREAK_81',
        metric: 'STREAK',
        threshold: 81,
        title: '81-Day Streak',
        description: 'Maintain an 81-day streak',
        order: 4,
    },
    {
        key: 'FIRST_JOURNEY',
        metric: 'JOURNEYS',
        threshold: 1,
        title: 'First Journey',
        description: 'Complete your first journey',
        order: 3,
    },
    {
        key: 'STORY_EXPLORER',
        metric: 'STORIES',
        threshold: 10,
        title: 'Story Explorer',
        description: 'Complete 10 stories',
        order: 4,
    },
    {
        key: 'JOURNEY_COLLECTOR',
        metric: 'JOURNEYS',
        threshold: 3,
        title: 'Journey Collector',
        description: 'Complete 3 journeys',
        order: 5,
    },
];
export const achievementSchema = z.object({
    key: z.string(),
    title: z.string(),
    description: z.string(),
    metric: achievementMetricSchema,
    threshold: z.number().int(),
    order: z.number().int(),
    /** Null while still locked. */
    unlockedAt: isoDateSchema.nullable(),
    /** Where the user is against the threshold right now, for the progress ring. */
    progress: z.number().int(),
});
export const achievementsResponseSchema = z.object({
    items: z.array(achievementSchema),
    unlockedCount: z.number().int(),
});
/**
 * Streak milestones — artboard 3d, which auto-shows the moment one is reached.
 *
 * Powers of three: 1, 3, 9, 27, 81, 243… A rule rather than a list, so it never runs out
 * and nobody has to remember to extend it. Chosen 2026-09-28, replacing CLAUDE.md §2.4's
 * 3/7/14/21 — the achievements above were realigned to match, so every celebration is also
 * an achievement and there is no longer a 7-day badge with nothing to show for it.
 */
export function isStreakMilestone(streak) {
    if (!Number.isInteger(streak) || streak < 1)
        return false;
    let value = streak;
    while (value % 3 === 0)
        value /= 3;
    return value === 1;
}
/** The next milestone strictly above `streak`. 0 -> 1, 1 -> 3, 4 -> 9. */
export function nextStreakMilestone(streak) {
    let value = 1;
    while (value <= streak)
        value *= 3;
    return value;
}
/**
 * The run of milestones 3d draws as a track, ending at the one still ahead.
 *
 * Walks backwards from the next milestone so the current one is always on screen, then
 * extends forward if the user is early enough that there is nothing behind them.
 */
export function streakMilestoneWindow(streak, size = 5) {
    const window = [];
    for (let value = nextStreakMilestone(streak); window.length < size && value >= 1; value /= 3) {
        window.unshift(value);
    }
    while (window.length < size)
        window.push((window[window.length - 1] ?? 1) * 3);
    return window;
}
/**
 * Artboard 3c — the streak screen, opened from Home's flame pill.
 *
 * Separate from ProgressStats because it needs history the Progress screen does not: when
 * the current run began, and which days of this week were earned. Both are derived, never
 * stored — the user row keeps only a count and the date of the last completion.
 */
export const streakDaySchema = z.object({
    /** YYYY-MM-DD in the USER's timezone, never UTC. */
    date: z.string(),
    done: z.boolean(),
});
export const streakResponseSchema = z.object({
    current: z.number().int(),
    longest: z.number().int(),
    /**
     * The local date this run began, or null when there is no run. Derived by counting the
     * streak back from the last completion rather than stored, so it cannot disagree with
     * the count it is drawn beside.
     */
    startedOn: z.string().nullable(),
    /** Monday through Sunday of the week containing the user's today. Always 7. */
    week: z.array(streakDaySchema).length(7),
});
/** The Progress screen. CLAUDE.md is explicit that it shows these and nothing more. */
export const progressStatsSchema = z.object({
    currentStreak: z.number().int(),
    longestStreak: z.number().int(),
    journeysStarted: z.number().int(),
    journeysCompleted: z.number().int(),
    storiesCompleted: z.number().int(),
    daysCompleted: z.number().int(),
});
//# sourceMappingURL=achievement.js.map